import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useStore } from '@/contexts/StoreContext';
import { launcherService, type ChangelogVersion } from '@/lib/services/launcher.service';
import { History, Loader2, Plus, Minus, Wrench, Sparkles } from 'lucide-react';
import { Caixa } from '@/construtor/caixa';

const ROTULO: Record<string, string> = {
  mod_added: 'Mod novo',
  mod_removed: 'Mod removido',
  modpack_changed: 'Modpack',
  identity_changed: 'Identidade',
  branding_changed: 'Aparência',
};

const ICONE: Record<string, React.ElementType> = {
  mod_added: Plus,
  mod_removed: Minus,
};

/**
 * O changelog público da loja.
 *
 * Location: src/pages
 * Why: existe além da tela do launcher por um motivo concreto — **link**. O
 * lojista cola no Discord dele, fixa no anúncio, manda para quem perguntou o
 * que mudou. Um changelog que só vive dentro do launcher não é compartilhável,
 * e a pergunta "o que mudou?" quase sempre acontece fora dele.
 *
 * Aceita `?version=` para o launcher abrir direto na versão que o jogador
 * acabou de instalar, em vez de largá-lo no topo de uma lista.
 */
export default function ChangelogPage() {
  const { store } = useStore();
  const [params] = useSearchParams();
  const [versions, setVersions] = useState<ChangelogVersion[] | null>(null);
  const [loading, setLoading] = useState(true);

  const versaoPedida = params.get('version') ?? undefined;

  useEffect(() => {
    if (!store?.id) return;

    let ativo = true;

    launcherService
      .getChangelog(store.id, versaoPedida)
      .then((resposta) => ativo && setVersions(resposta.versions))
      .catch(() => ativo && setVersions([]))
      .finally(() => ativo && setLoading(false));

    return () => {
      ativo = false;
    };
  }, [store?.id, versaoPedida]);

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-10">
      <Caixa id="download.mudancas.cabecalho">
      <header className="mb-8 flex items-center gap-3">
        <History className="h-7 w-7 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">O que mudou</h1>
          <p className="text-sm text-muted-foreground">
            {versaoPedida
              ? `Mudanças da versão ${versaoPedida}`
              : 'Histórico de atualizações do launcher'}
          </p>
        </div>
      </header>
      </Caixa>

      {loading && (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Carregando…
        </div>
      )}

      {!loading && (!versions || versions.length === 0) && (
        <p className="text-muted-foreground">
          Ainda não há mudanças registradas.
        </p>
      )}

      <Caixa id="download.mudancas.lista">
      <div className="space-y-8">
        {versions?.map((versao) => (
          <Caixa id="download.mudancas.versao" repetida key={versao.version}>
          <section key={versao.version}>
            <div className="mb-3 flex items-baseline gap-3 border-b pb-2">
              <h2 className="font-mono text-lg font-semibold">{versao.version}</h2>
              {versao.publishedAt && (
                <span className="text-xs text-muted-foreground">
                  {new Date(versao.publishedAt).toLocaleDateString('pt-BR')}
                </span>
              )}
            </div>

            {versao.store.length > 0 && (
              <ul className="space-y-2">
                {versao.store.map((item, indice) => {
                  const Icone = ICONE[item.kind] ?? Sparkles;

                  return (
                    <li key={`${item.target}-${indice}`} className="flex gap-2 text-sm">
                      <Icone className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                      <span>
                        <span className="text-xs uppercase tracking-wide text-muted-foreground">
                          {ROTULO[item.kind] ?? item.kind}
                        </span>{' '}
                        <span className="font-mono text-xs">{item.target}</span>
                        {item.note && (
                          <span className="text-muted-foreground"> — {item.note}</span>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}

            {/*
              As correções do launcher aparecem no mesmo bloco de versão, mas
              destacadas: para o jogador é tudo "mudou quando atualizei", e
              separar em outra página o obrigaria a entender uma divisão que é
              nossa, não dele.
            */}
            {versao.core.length > 0 && (
              <div className="mt-4 rounded-lg bg-muted/40 p-4">
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                  No launcher
                </p>
                <ul className="space-y-1.5">
                  {versao.core.map((nota, indice) => (
                    <li key={indice} className="flex gap-2 text-sm text-muted-foreground">
                      {nota.kind === 'fix' ? (
                        <Wrench className="mt-0.5 h-4 w-4 shrink-0" />
                      ) : (
                        <Sparkles className="mt-0.5 h-4 w-4 shrink-0" />
                      )}
                      {nota.text}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>
          </Caixa>
        ))}
      </div>
      </Caixa>
    </div>
  );
}
